import { useMemo, memo } from "react";
import {
  ChainOfThought,
  ChainOfThoughtHeader,
  ChainOfThoughtContent,
  ChainOfThoughtStep as COTStep,
} from "@/components/ai-elements/chain-of-thought";
import {
  adaptSteps,
  ChainOfThoughtStep,
  StepKey,
} from "./ChainOfThoughts.types";
import { AiResponseThoughtStep } from "@/v2/entities/chain-of-thoughts/ChainOfThoughts.model";
import {
  defaultRenderers,
  getLoadingMessage,
  getVisibleSteps,
} from "./ChainOfThoughts.rerender";
import styles from "./ChainOfThoughts.module.scss";
import {
  Search,
  Wrench,
  ArrowUpDown,
  Shield,
  Lightbulb,
  Globe,
  Brain,
  CheckCircle,
  Image as ImageIcon,
  Play,
  CheckCircle2,
  Info,
  Tag,
} from "lucide-react";

export type StepRenderer<K extends StepKey> = (args: {
  index: number;
  step: Extract<ChainOfThoughtStep, { stepKey: K }>;
  sources?: any[];
  isLastMessage?: boolean;
}) => JSX.Element;

export type RendererMap = {
  [K in StepKey]?: StepRenderer<K>;
};

export type ChainOfThoughtsProps = {
  title?: string;
  steps: (ChainOfThoughtStep | AiResponseThoughtStep)[];
  defaultOpen?: boolean;
  className?: string;
  currentStepIndex?: number;
  sources?: any[];
  isLoading?: boolean;
  loadingMessage?: string;
  hasTopic?: boolean;
  collapsible?: boolean;
  isLastMessage?: boolean;
};

function labelOf(stepKey: StepKey): string {
  switch (stepKey) {
    case "tools":
      return "Available skills";
    case "context":
      return "Sources search";
    case "rerank":
      return "Sources picking";
    case "strict":
      return "Strict mode check";
    case "plan":
      return "Planning";
    case "detect-language":
      return "Language detection";
    case "thinking":
      return "Auto routing analysis";
    case "prereqs":
      return "Prerequisites check";
    case "images":
      return "Image Processing";
    case "tool-invocation":
      return "Skill usage";
    case "tool-response":
      return "Skill response";
    case "additional-context":
      return "Additional context";
    case "topic":
      return "Topic classification";
    default:
      return String(stepKey);
  }
}

function iconOf(stepKey: StepKey) {
  switch (stepKey) {
    case "tools":
      return Wrench;
    case "context":
      return Search;
    case "rerank":
      return ArrowUpDown;
    case "strict":
      return Shield;
    case "plan":
      return Lightbulb;
    case "detect-language":
      return Globe;
    case "thinking":
      return Brain;
    case "prereqs":
      return CheckCircle;
    case "images":
      return ImageIcon;
    case "tool-invocation":
      return Play;
    case "tool-response":
      return CheckCircle2;
    case "additional-context":
      return Info;
    case "topic":
      return Tag;
    default:
      return CheckCircle;
  }
}

function getRenderer<K extends StepKey>(
  stepKey: K,
  map: RendererMap
): StepRenderer<K> | undefined {
  return map[stepKey] as StepRenderer<K> | undefined;
}

function ChainOfThoughtsComponent({
  title = "Chain of thought",
  steps,
  defaultOpen = true,
  className,
  currentStepIndex = -1,
  sources,
  isLoading = false,
  loadingMessage,
  hasTopic = false,
  collapsible = true,
  isLastMessage = false,
}: ChainOfThoughtsProps) {
  const adaptedSteps = useMemo(() => {
    try {
      return adaptSteps(steps);
    } catch (error) {
      console.error("failed to adapt steps:", error);
      return [];
    }
  }, [steps]);

  const dynamicLoadingMessage = useMemo(
    () => getLoadingMessage(isLoading, hasTopic, adaptedSteps, loadingMessage),
    [isLoading, hasTopic, adaptedSteps, loadingMessage]
  );

  const visibleSteps = useMemo(
    () => getVisibleSteps(adaptedSteps),
    [adaptedSteps]
  );

  if (!isLoading && visibleSteps.length === 0) {
    return <></>;
  }

  return (
    <ChainOfThought defaultOpen={defaultOpen} className={className}>
      <ChainOfThoughtHeader disabled={isLoading} collapsible={collapsible}>
        {isLoading && dynamicLoadingMessage ? dynamicLoadingMessage : title}
      </ChainOfThoughtHeader>
      <ChainOfThoughtContent>
        {visibleSteps.map((step, i) => {
          const Renderer = getRenderer(step.stepKey, defaultRenderers);
          const Icon = iconOf(step.stepKey);
          let status: "complete" | "active" | "pending" = "complete";

          if (currentStepIndex >= 0) {
            if (i < currentStepIndex) {
              status = "complete";
            } else if (i === currentStepIndex) {
              status = "active";
            } else {
              status = "pending";
            }
          }

          return (
            <COTStep
              key={`${step.stepKey}-${i}`}
              icon={Icon}
              label={labelOf(step.stepKey)}
              status={status}
              collapsible={true}
              defaultCollapsed={false}
            >
              {Renderer ? (
                <Renderer index={i} step={step} sources={sources} isLastMessage={isLastMessage} />
              ) : (
                <pre className={styles.pre}>
                  {JSON.stringify(step, null, 2)}
                </pre>
              )}
            </COTStep>
          );
        })}

        {isLoading && (
          <COTStep
            key="loading"
            icon={Brain}
            label={dynamicLoadingMessage || "Loading..."}
            status="active"
          >
            <div className={styles.loadingContainer}>
              <div className={styles.loadingDots}>
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </COTStep>
        )}
      </ChainOfThoughtContent>
    </ChainOfThought>
  );
}

export default memo(ChainOfThoughtsComponent);
